/**
 * CrossSellSection — "Complete your purchase with:" section on PDP.
 *
 * Portable model — accepts products from any source (SCAPI recommendations,
 * CIO recommendations, Builder-configured). The component only cares about
 * rendering a list of products with checkboxes, quantities, and a total.
 *
 * Source references:
 * - sfcc-website/org_hec/cartridge/templates/default/product/components/recomendation.isml
 * - Production screenshot: /tmp/pdp-hsa-tums-top.png
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router";

import { Skeleton } from "~/components/ui/skeleton";
import { useSite, useURL } from "~/contexts";
import { emitProductClick } from "~/hec/analytics/event-layer/gtm-product-click-events";
import { QuantityStepper } from "~/hec/components/ui/quantity-stepper";
import { useTrackRecsClick } from "~/hec/hooks/use-track-recs-click";
import { useTrackRecsView } from "~/hec/hooks/use-track-recs-view";
import { buildProductUrl } from "~/hec/utils/product-slug";
import { resolveQuantityCap } from "~/lib/quantity-cap";
import { useFormatPrice } from "~/shop/utils/price";
import { resolveImageUrl } from "~/utils/salesforce-image-transform";

import type { CrossSellItem } from "../../resources/schemas/basket";

// GA4 product-click list identifiers for the PDP "Complete Your Purchase" recs.
// SFRA parity (dataLayerHelper.js:247-248). Mirrors the (un-exported) constants
// in pdp-shared.tsx + gtm-select-item-event.ts — the same `cyp_pdp` stream so
// click + view_item_list + select_item all report as one list in GA4 (SFCC-11318).
const CYP_LIST_ID = "cyp_pdp";
const CYP_LIST_NAME = "Complete Your Purchase";

export interface CrossSellProduct {
  id: string;
  name: string;
  price?: number;
  imageUrl?: string;
  url?: string;
  orderable?: boolean;
  /** Available-to-sell (`inventory.ats`) — caps the qty stepper at live stock. */
  ats?: number;
  /** CIO `data.brand` — feeds GA4 `view_item_list` item_brand (SFCC-11011). */
  brand?: string;
  /** CIO `data.variation_id` — feeds GA4 `view_item_list` item_variant (SFCC-11011). */
  variationId?: string;
}

interface CrossSellSectionProps {
  products: CrossSellProduct[];
  isLoading?: boolean;
  /** CIO pod for `recommendation_click` beacon attribution. */
  podId?: string;
  /** `result_id` from the CIO response that produced `products`. */
  resultId?: string;
  /** Per-product strategy IDs (parallel to `products`). */
  strategyIds?: string[];
  /**
   * Server-resolved category path per product id, from the CYP pod's
   * `useTileImpressions` batch one component up (SFCC-12175 W3). Cross-sell
   * products come from the CIO client SDK with no SCAPI hop (CIO Path A §2), so
   * they carry no `primaryCategoryId` of their own — without this the
   * `select_item` below emitted a blank `item_category`. Keyed by master id or
   * variant id.
   */
  categoryByPid?: Map<string, string>;
  /** Called whenever the checked-product totals change — used by parent to render totals in the buy form. */
  onTotalsChange?: (total: number, items: number) => void;
  /** Called whenever the checked-and-orderable selection changes — used by parent to pass items to basket.addMany. */
  onSelectionsChange?: (items: CrossSellItem[]) => void;
}

export function CrossSellSection({
  products,
  isLoading = false,
  podId,
  resultId,
  strategyIds,
  categoryByPid,
  onTotalsChange,
  onSelectionsChange,
}: CrossSellSectionProps) {
  const trackClick = useTrackRecsClick();
  const viewRef = useTrackRecsView({
    podId,
    resultId,
    itemIds: isLoading ? [] : products.map(product => product.id),
  });
  const url = useURL();
  const { site } = useSite();
  const formatPrice = useFormatPrice();

  // Track which products are selected + their quantities.
  //
  // The useState initializer runs once at mount when `products` is empty
  // (client-only CIO fetch hasn't resolved yet). We sync on every products
  // change so new entries default to checked=true. Existing entries are
  // preserved so user toggles/quantity edits aren't clobbered.
  const [selections, setSelections] = useState<
    Record<string, { checked: boolean; quantity: number }>
  >({});

  useEffect(() => {
    setSelections(prev => {
      const next = { ...prev };
      let changed = false;
      for (const p of products) {
        if (p.orderable === false) continue;
        if (!(p.id in next)) {
          next[p.id] = { checked: false, quantity: 1 };
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [products]);

  const toggleProduct = useCallback((id: string) => {
    setSelections(prev => ({
      ...prev,
      [id]: { ...prev[id], checked: !prev[id]?.checked },
    }));
  }, []);

  const setQuantity = useCallback((id: string, qty: number) => {
    setSelections(prev => ({
      ...prev,
      [id]: { ...prev[id], quantity: Math.max(1, Math.min(150, qty)) },
    }));
  }, []);

  // Compute totals
  const selectedProducts = products.filter(
    p => selections[p.id]?.checked && p.orderable !== false,
  );
  const totalPrice = selectedProducts.reduce(
    (sum, p) => sum + (p.price ?? 0) * (selections[p.id]?.quantity ?? 1),
    0,
  );
  const totalItems = selectedProducts.reduce(
    (sum, p) => sum + (selections[p.id]?.quantity ?? 1),
    0,
  );

  useEffect(() => {
    onTotalsChange?.(totalPrice, totalItems);
  }, [totalPrice, totalItems, onTotalsChange]);

  useEffect(() => {
    const items = selectedProducts.map(p => ({
      productId: p.id,
      quantity: selections[p.id]?.quantity ?? 1,
    }));
    onSelectionsChange?.(items);
  }, [selectedProducts, selections, onSelectionsChange]);

  if (isLoading) {
    return <CrossSellSkeleton />;
  }

  if (products.length === 0) return null;

  return (
    <div ref={viewRef} className="cross-sell-section mt-4 w-full">
      <h3 className="mb-3 font-grotesque text-sm font-light text-emphasis">
        Complete your purchase with:
      </h3>

      <div className="flex flex-col gap-2.5">
        {products.map((product, i) => {
          const isSoldOut = product.orderable === false;
          const sel = selections[product.id];
          const productUrl =
            product.url ?? buildProductUrl(product.id, product.name);
          // Single handler reused by both the image Link and the name Link
          // on each row. Only one fires per navigation.
          const fireClickBeacon = () => {
            // GA4/UA product-click (productClick + GA4_productClick) — fires on
            // every CYP tile click regardless of pod, list `cyp_pdp`. id=master,
            // variant=CIO variation_id (SFCC-11318 parity with the view_item_list
            // + select_item streams). emitProductClick no-ops without a listId.
            emitProductClick(
              {
                id: product.id,
                name: product.name,
                brand: product.brand,
                variant: product.variationId,
                price: product.price,
                // SFCC-12175 W3 — the impressions batch resolved this
                // server-side; look up by master id, then variant id.
                category:
                  categoryByPid?.get(product.id) ??
                  (product.variationId
                    ? categoryByPid?.get(product.variationId)
                    : undefined) ??
                  "",
              },
              {
                listId: CYP_LIST_ID,
                listName: CYP_LIST_NAME,
                index: i + 1,
                siteId: site.id,
              },
            );
            if (!podId) return;
            trackClick({
              podId,
              itemId: product.id,
              itemName: product.name,
              strategyId: strategyIds?.[i],
              resultId,
              resultPositionOnPage: i + 1,
              numResultsPerPage: products.length,
            });
          };

          return (
            // Proportional 12-col layout mirroring SFRA's grid
            // (recomendation.isml): col-1 checkbox / col-3 image (capped at
            // 110px) / col-8 name+price. Widths are fractional so spacing
            // scales per breakpoint; 10px col padding = the gutters.
            <div
              key={product.id}
              className="flex items-center"
              data-testid={`pdp-cross-sell-row-${product.id}`}
            >
              {/* col-1 (8.33%): checkbox, flush-left */}
              <div className="flex w-1/12 shrink-0 pr-2.5">
                <input
                  type="checkbox"
                  checked={sel?.checked ?? false}
                  disabled={isSoldOut}
                  onChange={() => toggleProduct(product.id)}
                  className="h-4 w-4 shrink-0 rounded border-gray-300"
                  aria-label={`Include ${product.name}`}
                  data-testid={`pdp-cross-sell-checkbox-${product.id}`}
                />
              </div>

              {/* Fixed 90px media rail: SFRA serves 90px thumbs here. */}
              <Link
                to={url(productUrl)}
                onClick={fireClickBeacon}
                className="block w-[90px] shrink-0 px-2.5"
                data-testid={`pdp-cross-sell-image-${product.id}`}
              >
                {product.imageUrl ? (
                  <img
                    src={resolveImageUrl(product.imageUrl)}
                    alt={product.name}
                    className="aspect-square w-full object-contain"
                    loading="lazy"
                  />
                ) : (
                  <div className="aspect-square flex w-full items-center justify-center text-xs text-gray-400">
                    No img
                  </div>
                )}
              </Link>

              {/* col-8 (rest): name + price. px-2.5 — pl gutters from the image,
                  pr spaces the standalone qty stepper that follows. */}
              <div className="flex flex-1 flex-col px-2.5">
                <Link
                  to={url(productUrl)}
                  onClick={fireClickBeacon}
                  className="mb-[5px] text-sm font-light leading-[1.57] text-emphasis"
                  data-testid={`pdp-cross-sell-name-${product.id}`}
                >
                  {product.name}
                </Link>
                {isSoldOut ? (
                  <span
                    className="text-xs font-medium text-red-500"
                    data-testid={`pdp-cross-sell-price-${product.id}`}
                  >
                    Sold Out
                  </span>
                ) : (
                  <span
                    className="text-xs font-medium text-emphasis"
                    data-testid={`pdp-cross-sell-price-${product.id}`}
                  >
                    {formatPrice(product.price)}
                  </span>
                )}
              </div>

              {/* Quantity stepper — shown on the right once the row is checked */}
              {!isSoldOut && sel?.checked && (
                <QuantityStepper
                  quantity={sel?.quantity ?? 1}
                  onChange={n => setQuantity(product.id, n)}
                  max={resolveQuantityCap(product.ats)}
                  mode="standalone"
                  testIdPrefix={`pdp-cross-sell-quantity-${product.id}`}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function CrossSellSkeleton() {
  return (
    <div className="cross-sell-section mt-4 w-full">
      <Skeleton className="mb-3 h-4 w-48" />
      {/* Mirrors the live row's proportional cols (w-1/12 / w-1/4 capped /
          flex-1) so the skeleton -> content swap doesn't shift layout. */}
      <div className="flex flex-col gap-2.5">
        {[0, 1].map(i => (
          <div key={i} className="flex items-center">
            <div className="flex w-1/12 shrink-0 pr-2.5">
              <Skeleton className="h-4 w-4 rounded" />
            </div>
            <div className="w-[90px] shrink-0 px-2.5">
              <Skeleton className="aspect-square w-full" />
            </div>
            <div className="flex flex-1 flex-col gap-1 pl-2.5">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-3 w-16" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
